

// ==================================
// DSE Subject-related exports
// ==================================

import { categoryAGrades, categoryBGrades, csdGrades, categoryCGrades, koreanGrades, japaneseGrades, urduGrades } from "./lib/data/subjects/constants";
import { getSubjectByName } from "./lib/data/subjects/subjects";
import { getSubjectCategory } from "./lib/utils/subjectCategories";

// Get university name from JUPAS ID
export const getUniversityName = (jupasId: string): string => {
    const firstDigit = jupasId.charAt(0);
    switch (firstDigit) {
        case '1': return 'CityU';
        case '2': return 'HKBU';
        case '3': return 'PolyU';
        case '4': return 'CUHK';
        case '5': return 'HKUST';
        case '6': return 'HKU';
        case '7': return 'Lingnan';
        case '8': return 'EdUHK';
        case '9': return 'HKMU';
        case 'S': return 'SSSDP';
        default:
            // Check for special institution prefixes (JSSA, JSSB, etc.)
            if (jupasId.startsWith('JSSA')) return 'SFU';
            if (jupasId.startsWith('JSSB')) return 'HKSYU';
            if (jupasId.startsWith('JSSC')) return 'HKCHC';
            if (jupasId.startsWith('JSSD')) return 'HSUHK';
            if (jupasId.startsWith('JSSF')) return 'UOW';
            if (jupasId.startsWith('JSSG')) return 'THEi';
            if (jupasId.startsWith('JSSH')) return 'HSUHK';
            return 'Unknown';
    }
};

// Get university key for INSTITUTIONS_DATA lookup
export const getUniversityKey = (jupasId: string): string => {
    if (!jupasId) return '';
    const firstChar = jupasId.charAt(0);

    // For SSSDP programmes (starting with 'S'), return first 2 characters as inst_prefix
    if (firstChar === 'S' && jupasId.length >= 2) {
        return jupasId.substring(0, 2); // Returns 'SA', 'SC', 'SD', 'SF', 'SG', 'SH', 'SY', etc.
    }

    // For standard programmes, return the first character (institution prefix)
    switch (firstChar) {
        case '1': return '1'; // CityU
        case '2': return '2'; // HKBU
        case '3': return '3'; // PolyU
        case '4': return '4'; // CUHK
        case '5': return '5'; // HKUST
        case '6': return '6'; // HKU
        case '7': return '7'; // Lingnan
        case '8': return '8'; // EdUHK
        case '9': return '9'; // HKMU
        default: return '';
    }
};

export type ScoreTriple = { current: string; min: string; max: string };

// Helper function to translate grade display
export function translateGrade(grade: string, language: string): string {
    if (grade === "Attained") return "A";
    if (grade === "Unattained") return "U";
    return grade;
}

// Helper function to get grade options based on subject
export function getGradeOptionsForSubject(subjectName: string): string[] {
    if (subjectName === "Citizenship and Social Development") return csdGrades;
    if (subjectName === "Korean") return koreanGrades;
    if (subjectName === "Japanese") return japaneseGrades;
    if (subjectName === "Urdu") return urduGrades;
    if (["French", "German", "Spanish"].includes(subjectName)) return categoryCGrades;
    if (getSubjectCategory(subjectName) === "B" || getSubjectByName(subjectName)?.category === "B") return categoryBGrades;
    return categoryAGrades;
}

export const CORE_SUBJECTS = [
    { subject: "Chinese Language" },
    { subject: "English Language" },
    { subject: "Mathematics" },
    { subject: "Citizenship and Social Development" },
];

// Subject categorization for electives
export const subjectCategories = {
    Science: [
        "Biology",
        "Chemistry",
        "Health Management and Social Care",
        "Physics",
        "Combined Science (Chemistry, Biology)",
        "Combined Science (Physics, Chemistry)",
        "Combined Science (Biology, Physics)",
        "Technology and Living",
        "Information and Communication Technology",
        "Design and Applied Technology"
    ],
    Mathematics: [
        "Mathematics Extended Part Module 1",
        "Mathematics Extended Part Module 2"
    ],
    Society: [
        "Chinese History", "History", "Geography",
        "Economics", "Ethics and Religious Studies"
    ],
    Arts: [
        "Chinese Literature",
        "English Literature",
        "Visual Arts",
        "Music"
    ],
    Commerce: [
        "Business, Accounting and Financial Studies",
        "Tourism and Hospitality Studies"
    ],
    Sports: [
        "Physical Education"
    ],
    "Applied Learning - Creative Studies": [
        "Creative Studies",
        "Cultural and Creative Product Design",
        "Fashion Image Design",
        "Interior and Exhibition Design/Interior and Event Spatial Design",
        "Jewellery Design in Digital Age",
        "Computer Game and Animation Design",
        "Digital Comic Design and Production",
        "Popular Music Production",
        "Taking a Chance on Dance",
        "The Essentials of Theatre Arts"
    ],
    "Applied Learning - Media & Communication": [
        "Digital Media and Radio Production",
        "Film and Transmedia/Film Production",
        "Digital Brand Communication/Digital Advertising",
        "Multimedia Storytelling",
        "PR and Multimedia Communication",
        "Applied Learning (Vocational English) - English Communication",
        "Applied Learning (Vocational English) - English for Business Services",
        "Applied Learning (Vocational English) - English for Service Professionals",
        "Creative English - Cultural Tourism",
        "Creative English - Biz and Media",
        "Creative English - PR and Marketing",
        "Practical Translation (CHIN-ENG)",
        "Applied Japanese and Japanese Culture",
        "Everyday Japanese and Japanese Culture",
        "Korean Language and Culture"
    ],
    "Applied Learning - Business, Management & Law": [
        "Accounting in Action",
        "Accounting for e-Business",
        "AI in Business",
        "Data Application for Business/AI Application in Business Data",
        "Innovation and Entrepreneurship",
        "Introduction to Fintech",
        "KOL Marketing and Generative AI",
        "Marketing and Online Promotion",
        "Modern Logistics Studies",
        "Law Enforcement in Hong Kong"
    ],
    "Applied Learning - Services": [
        "Modern Southeast Asian Cuisine",
        "Pâtisserie and Café Operations",
        "Western Cuisine",
        "Airport Passenger Terminal Operations/Airport Operations and Services",
        "Hospitality Services in Practice",
        "Hotel Operations",
        "Child Care and Development",
        "Child Care and Education",
        "Fundamental Cosmetology",
        "Gerontech and Services"
    ],
    "Applied Learning - Applied Science": [
        "Food Technology and Nutrition",
        "Foundation in Chinese Medicine",
        "Health Care Practice",
        "Medical Laboratory Science",
        "Pet Care and Grooming",
        "Rehabilitation Care Practice",
        "Applied Psychology",
        "Practical Psychology",
        "Exercise and Fitness Coaching",
        "Exercise Science and Health Fitness"
    ],
    "Applied Learning - Engineering & Production": [
        "Digital Construction",
        "Electrical and Energy Engineering",
        "AI and Robotics",
        "Computer Forensic Technology",
        "Cybersecurity",
        "eSports Technology and Management",
        "Tech Basics",
        "Aviation Studies",
        "Railway Studies"
    ],
    Languages: [
        "French",
        "German",
        "Japanese",
        "Korean",
        "Spanish",
        "Urdu"
    ]
};
